import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { Order } from "../../../domain/entities/order/Order";
import { Question } from "../../../domain/entities/question/Question";
import { FeedbackRepositoryImpl } from "../../domain/feedbackRepositoryImpl/FeedbackRepositoryImpl";
import { queryClientContext } from "../hooks/useQueryClient";
import { useStaticInfo } from "../hooks/useStaticInfo";

type QuestionMap = Record<string, Question[]>;

const feedbackRepo = FeedbackRepositoryImpl.getInstance();

const useViewQuestionsFromOrderProducts = (order: Order | undefined): QuestionMap => {
  const {
    session: { auth },
    apiUrl,
  } = useStaticInfo();

  const uniqueVariantIds = order
    ? order.items.reduce<string[]>((acc, current) => {
        if (!acc.includes(current.variantId)) {
          acc.push(current.variantId);
        }
        return acc;
      }, [])
    : [];

  const queryClient = useContext(queryClientContext);

  const { data } = useQuery(
    {
      queryKey: [`useViewQuestionsFromOrderProducts-${order?.orderId}`],
      queryFn: async () => {
        const questionList = await Promise.all(
          uniqueVariantIds.map((variant) => {
            return feedbackRepo.getOrderFeedback(auth, variant, apiUrl);
          }),
        );

        const questionsPerVariant = uniqueVariantIds.reduce<QuestionMap>(
          (acc, currentId, currentIndex) => ({ ...acc, [currentId]: questionList[currentIndex] as Question[] }),
          {},
        );

        return questionsPerVariant;
      },
      enabled: Boolean(auth) && Boolean(order),
    },
    queryClient,
  );

  return data as QuestionMap;
};

export { useViewQuestionsFromOrderProducts };
